<script setup lang="ts">
import TabBar from '@components/util/TabBar.vue';
import { SearchMode } from '@utils/types';
import SearchReserveForm from '@components/util/search/SearchReserveForm.vue';
import { ReserveType } from '@utils/itinerary';
import { type Radio } from '#imports';
import { type FlightSearchForm } from '@search/trip/flight/FlightSearchForm';

const form = useTripSearchForm<FlightSearchForm>(false);
const route = useRoute();
const { isMrbilit } = useWhiteLabel();

const searchModeModel = computed({
    get(): number {
        return form.searchMode.value === SearchMode.Domestic ? 0 : 1;
    },
    set(value: number) {
        form['onUpdate:searchMode'](
            value ? SearchMode.Foreign : SearchMode.Domestic
        );
    },
});

const flightOptions: (Radio<SearchMode> & { title: string })[] = [
    {
        label: 'پرواز داخلی',
        title: 'پرواز داخلی',
        value: SearchMode.Domestic,
    },
    {
        label: 'پرواز خارجی',
        title: 'پرواز خارجی',
        value: SearchMode.Foreign,
    },
];

onServerPrefetch(() => {
    if (route.path !== '/' || isMrbilit.value) {
        return showError({ statusCode: 404 });
    }
});
</script>

<template>
    <div class="index-root">
        <div class="search-bg">
            <h1 class="search-bg-title">یک جستجو، همه پروازها</h1>
            <SearchReserveForm
                v-bind="form"
                :reserve-type="ReserveType.Flight"
                origin-title="فرودگاه مبدأ"
                destination-title="فرودگاه مقصد"
                :location-debounce-search="
                    form.searchMode.value !== SearchMode.Domestic
                "
                :search-mode-options="flightOptions"
            >
                <template #mode-selector>
                    <div class="full-bar">
                        <mr-divider
                            thickness="1px"
                            color="#bec9d4"
                            class="mobile-divider"
                        />
                        <TabBar
                            v-model="searchModeModel"
                            class="tabs"
                            :tabs="flightOptions"
                        />
                        <mr-divider thickness="1px" color="#bec9d4" />
                    </div>
                </template>
            </SearchReserveForm>
        </div>
        <div class="features-container">
            <div class="feature">
                <img
                    src="/airplaneticket/img/features/insurance.png"
                    alt="بیمه کنسلی پرواز"
                    class="feature-icon"
                />
                <div class="feature-title">بیمه اختصاصی کنسلی پرواز</div>
                <div class="feature-text">
                    ایرپلین تیکت با بیمه‌کردن پرواز شما اطمینان حاصل می‌کند که
                    علی‌رغم کنسلی پرواز شرکت‌های هواپیمایی، سفر شما محقق می‌شود.
                </div>
            </div>
            <div class="feature">
                <img
                    src="/airplaneticket/img/features/replacement.png"
                    alt="ارائه بلیط جایگزین تا دو برابر مبلغ پرداختی"
                    class="feature-icon"
                />
                <div class="feature-title">ضمانت اختصاصی پرواز چارتری</div>
                <div class="feature-text">
                    در صورت بروز مشکل، ایرپلین تیکت جهت تهیه بلیط جایگزین، تا دو
                    برابر مبلغ اولیه را بازپرداخت خواهد نمود.
                </div>
            </div>
            <div class="feature">
                <img
                    src="/airplaneticket/img/features/support.png"
                    alt="پشتیبانی 42 ساعته"
                    class="feature-icon"
                />
                <div class="feature-title">پشتیبانی شبانه روزی</div>
                <div class="feature-text">
                    برای ساختن یک تجربه موفق از خرید بلیط آنلاین در تمامی مراحل
                    خرید و به صورت شبانه روزی همراه شما خواهیم بود.
                </div>
            </div>
        </div>
        <div class="rights-container">
            <div class="rights-text">
                <div class="rights-text-title">حقوق خود را بشناسیم!</div>
                <div class="rights-text-body">
                    یکی از بهترین روش‌ها برای بهبود کیفیت خدمات حمل و نقل هوایی
                    آشنایی مسافران با «حقوق مسافر» است. ایرپلین تیکت، ضمن دعوت
                    به خواندن این حقوق، آماده مشاوره و پاسخگویی به ابهامات شما
                    می‌باشد.
                </div>
                <mr-button
                    href="/airplaneticket/pax-rights.pdf"
                    target="_blank"
                    class="pax-pdf"
                    link
                >
                    دریافت آیین‌نامه حقوق مسافر
                </mr-button>
            </div>
            <div class="rights-logos">
                <a href="https://www.cao.ir/" rel="nofollow" target="_blank">
                    <img
                        src="/airplaneticket/img/rights/cao.png"
                        alt="سازمان هواپیمایی کشوری"
                    />
                </a>
                <a
                    href="https://www.cao.ir/paxrights"
                    rel="nofollow"
                    target="_blank"
                >
                    <img
                        src="/airplaneticket/img/rights/qr.png"
                        alt="دریافت حقوق مسافر"
                    />
                </a>
                <a href="http://www.aira.ir" rel="nofollow" target="_blank">
                    <img
                        src="/airplaneticket/img/rights/aira.png"
                        alt="دامنه نرخ بلیط شرکت های هواپیمایی"
                    />
                </a>
                <a
                    href="https://logo.samandehi.ir/Verify.aspx?id=26015&p=uiwkgvkaobpdrfthdshw"
                    target="_blank"
                >
                    <img
                        id="nbqefukzesgtjxlzapfu"
                        referrerpolicy="origin"
                        style="cursor: pointer"
                        alt="logo-samandehi"
                        src="https://logo.samandehi.ir/logo.aspx?id=26015&p=odrfwlbqlymanbpdujyn"
                    />
                </a>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.index-root {
    .search-bg {
        height: 450px;
        background: url(/airplaneticket/img/search-bg.jpg) center;
        background-size: cover;
        margin-bottom: 110px;
        display: flex;
        flex-direction: column;
        align-items: center;
        @include mobile-only {
            margin-bottom: 360px;
            height: 250px;
        }

        .search-bg-title {
            margin: 140px 0 0 0;
            padding-top: 86px;
            padding-bottom: 37px;
            @include tg-display1($color: white);
            text-align: center;
            @include mobile-only {
                margin: 0;
            }
        }

        :deep(.search-form-wrapper) {
            background-color: #014189;

            .form-header {
                flex-wrap: wrap;
                @include mobile-only {
                    height: auto;
                }

                .full-bar {
                    flex-basis: 100%;
                    flex-shrink: 0;
                    display: flex;
                    align-items: flex-end;
                    margin-bottom: 32px;
                    .mobile-divider {
                        @include desktop-only {
                            display: none;
                        }
                    }

                    .tabs {
                        white-space: nowrap;
                        .tab-container {
                            border-bottom: 1px solid #bec9d4;
                            gap: 40px;
                        }

                        .tab {
                            color: white;
                            border-bottom: none;

                            &.active {
                                color: #d6b469;
                                border-bottom: 3px solid;
                                border-color: #d6b469;
                            }
                        }
                    }
                }
                .desktop-only {
                    display: flex !important;
                }
                .mobile-only {
                    display: none !important;
                }

                .mr-checkbox {
                    .checkbox-label {
                        color: white;
                    }

                    .icon {
                        background-color: rgba(214, 180, 105, 0.35) !important;
                        border-color: #d6b469 !important;
                    }

                    &.active {
                        .checkbox-label {
                            color: #d6b469;
                        }

                        .icon {
                            background-color: #d6b469 !important;
                            border-color: #d6b469 !important;
                        }
                    }
                }
            }
            .counter-value {
                color: #014189;
            }

            .mr-button {
                &.filled {
                    background-color: #d6b469 !important;
                }

                &.transparent {
                    color: #d6b469 !important;

                    .icon {
                        color: #d6b469 !important;
                    }
                }

                &.bordered {
                    color: #d6b469 !important;
                    border-color: #d6b469 !important;

                    .icon {
                        color: #d6b469 !important;
                    }
                }
            }

            .day-wrapper.selected .day-container {
                background-color: #014189;
            }
        }
    }
    .features-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        max-width: 1200px;
        margin-inline: auto;
        @include mobile-only {
            flex-direction: column;
            gap: 80px;
            padding-bottom: 80px;
            padding-top: 48px;
        }
        .feature {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 33.33%;
            padding: 60px 10px;
            gap: 10px;
            @include mobile-only {
                width: 100%;
                padding-block: 0;
            }
            .feature-icon {
                width: 110px;
            }
            .feature-title {
                @include tg-m($w7, #d6b469);
                text-align: center;
            }
            .feature-text {
                @include tg-s($w7, #014189);
                text-align: center;
                padding: 0 26px;
            }
        }
    }
    .rights-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        max-width: 1200px;
        margin-inline: auto;
        @include mobile-only {
            flex-direction: column-reverse;
            gap: 30px;
        }
        .rights-text {
            flex-basis: 50%;
            display: flex;
            flex-direction: column;
            gap: 16px;
            @include mobile-only {
                flex-basis: 100%;
                align-items: center;
            }
            .rights-text-title {
                @include tg-xl($w6, #014189);
            }
            .rights-text-body {
                @include tg-m($color: #014189);
            }
            .pax-pdf {
                background-color: #014189;
                @include desktop-only {
                    align-self: flex-start;
                }
            }
        }
        .rights-logos {
            flex-basis: 50%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 32px;
            @include mobile-only {
                flex-basis: 100%;
                flex-wrap: wrap;
                justify-content: center;
            }
            img {
                width: 100px;
            }
        }
    }
}
</style>
